/**
 * Infer PMA identity policy fields from selected picker students (pure).
 */

import { PROGRAM_TYPES, PROGRAM_NAMES, CENTERS } from './pmaIdentityPolicy';
import { defaultDurationToken } from './pmaLifecyclePolicy';

const CENTER_BY_LETTER = Object.freeze({
  n: 'Noida',
  b: 'Bangalore',
  m: 'Mysuru',
  c: 'Chennai',
  r: 'Roorkee',
});

/**
 * Parse unix username like up_b1_abhishek_3 or po_b27n_sujeet_547.
 */
export function parseUnixUsernamePolicyHints(unixUsername) {
  const raw = String(unixUsername || '')
    .trim()
    .toLowerCase();
  if (!raw) return null;
  const match = raw.match(/^([a-z]{2})_b(\d+)([a-z]?)_/);
  if (!match) return null;
  const prefix = match[1].toUpperCase();
  const batchNo = String(Number(match[2]));
  const centerLetter = match[3] || '';
  const programType = PROGRAM_TYPES.includes(prefix) ? prefix : '';
  let mode = '';
  let center = '';
  if (centerLetter) {
    mode = 'Offline';
    center = CENTER_BY_LETTER[centerLetter] || '';
  } else {
    mode = 'Online';
    center = '';
  }
  return {
    programType,
    programName: programType ? PROGRAM_NAMES[programType] || '' : '',
    batchNo,
    mode,
    center,
  };
}

function extractBatchNoFromName(batchName, batchNumber) {
  if (batchNumber != null && String(batchNumber).trim() !== '') {
    const n = Number(String(batchNumber).replace(/\D/g, ''));
    if (Number.isFinite(n) && n > 0) return String(n);
  }
  const text = String(batchName || '');
  const batchWord = text.match(/batch[\s#_.:-]*0*(\d+)/i);
  if (batchWord) return String(Number(batchWord[1]));
  const b = text.match(/_b(\d+)/i) || text.match(/\bb(\d+)\b/i);
  if (b) return String(Number(b[1]));
  return '';
}

function normalizeCenter(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return CENTERS.find((c) => raw.toLowerCase().startsWith(c.toLowerCase())) || raw;
}

/**
 * Policy hints from one picker student row.
 */
export function policyHintsFromStudent(student) {
  if (!student || typeof student !== 'object') return {};
  const fromUnix = parseUnixUsernamePolicyHints(student.unixUsername);
  const categoryType = String(student.categoryType || '')
    .trim()
    .toUpperCase();
  const programType = PROGRAM_TYPES.includes(categoryType)
    ? categoryType
    : fromUnix?.programType || '';
  const batchMode = String(student.batchMode || '').trim();
  const mode =
    batchMode === 'Online' || batchMode === 'Offline'
      ? batchMode
      : fromUnix?.mode || '';
  const center =
    mode === 'Online'
      ? ''
      : normalizeCenter(student.batchLocation) || fromUnix?.center || '';
  const batchNo =
    extractBatchNoFromName(student.batchName, student.batchNumber) || fromUnix?.batchNo || '';

  const hints = {};
  if (programType) {
    hints.programType = programType;
    hints.programName = PROGRAM_NAMES[programType] || '';
  }
  if (batchNo) hints.batchNo = batchNo;
  if (mode) hints.mode = mode;
  if (mode === 'Offline' && center) hints.center = center;
  if (mode === 'Online') hints.center = '';
  return hints;
}

function unanimousValue(values) {
  const filled = values.map((v) => String(v ?? '').trim()).filter(Boolean);
  if (!filled.length) return '';
  const first = filled[0];
  return filled.every((v) => v === first) ? first : '';
}

/**
 * Merge hints from selected students — only fields where all agree.
 */
export function inferPmaPolicyFromStudents(students = []) {
  if (!Array.isArray(students) || students.length === 0) return {};
  const hintsList = students.map(policyHintsFromStudent);
  const keys = ['programType', 'programName', 'batchNo', 'mode', 'center'];
  const out = {};
  for (const key of keys) {
    const value = unanimousValue(hintsList.map((h) => h[key]));
    if (value !== '' || key === 'center') {
      if (key === 'center' && out.mode === 'Online') {
        out.center = '';
        continue;
      }
      if (value) out[key] = value;
    }
  }
  if (out.programType && !out.programName) {
    out.programName = PROGRAM_NAMES[out.programType] || '';
  }
  if (out.mode === 'Online') out.center = '';
  return out;
}

/**
 * Apply inferred hints onto current policy without wiping lab-only fields.
 */
export function mergePolicyWithStudentHints(policy, hints) {
  if (!hints || !Object.keys(hints).length) return policy;
  const next = {
    ...policy,
    ...hints,
    linuxGroups: policy.linuxGroups,
    tools: policy.tools,
    vncFqdn: policy.vncFqdn,
    puttyFqdn: policy.puttyFqdn,
    userCategory: policy.userCategory,
    poSchedule: policy.poSchedule,
  };
  if (hints.programType && hints.programType !== policy.programType) {
    next.programDuration = defaultDurationToken({
      programType: next.programType,
      userCategory: next.userCategory,
      poSchedule: next.poSchedule,
    });
  }
  return next;
}
